import { ChevronDown } from '@/components/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  IMPLICIT_PRIMITIVE_LABELS,
  ImplicitPrimitive,
  ImplicitPrimitiveParams,
  NASA_LATTICE_DEFAULTS,
  NASA_LATTICE_TOPOLOGY_ORDER,
  NASA_LATTICE_DISABLED,
  NASA_LATTICE_HEROES,
  NasaLatticeTopology,
} from '@/config/implicitPrimitives';

import {
  pickerContentClass,
  pickerDisablableItemBaseClass,
  pickerHeroWrapperClass,
  pickerItemActiveClass,
  pickerItemDisabledClass,
  pickerItemInactiveClass,
  pillWidthClass,
} from '../dropdownStyles';

import { SubTab } from './data';

interface NasaLatticeDropdownProps {
  tab: SubTab;
  vehicleSubTab: string;
  implicitPrimitive: ImplicitPrimitive;
  setVehicleSubTab: (subTab: string) => void;
  setImplicitPrimitive: (p: ImplicitPrimitive) => void;
  setImplicitPrimitiveParams: (p: ImplicitPrimitiveParams) => void;
}

export function NasaLatticeDropdown({
  tab,
  vehicleSubTab,
  implicitPrimitive,
  setVehicleSubTab,
  setImplicitPrimitive,
  setImplicitPrimitiveParams,
}: NasaLatticeDropdownProps) {
  const isActive = vehicleSubTab === tab.value;
  const activeLatticeLabel =
    isActive && implicitPrimitive.startsWith('nasa-lattice-')
      ? IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive]
      : 'Select Topology';
  // Picking a topology auto-flips the conversation kernel to
  // 'unitcell' via the isNasaLatticePrimitives effect in
  // PromptView (matches how implicit-primitives forces
  // outputFormat='implicit' at generate time). The user does
  // not need to pre-select the kernel from the dropdown.
  const handlePick = (t: NasaLatticeTopology) => {
    if (NASA_LATTICE_DISABLED.has(t)) return;
    setVehicleSubTab('nasa-lattice-primitives');
    setImplicitPrimitive(t);
    setImplicitPrimitiveParams({
      kind: 'nasa-lattice',
      topology: t,
      params: NASA_LATTICE_DEFAULTS,
    });
  };
  // Always show a hero: the active topology's, else the first heroed
  // topology in the list — mirrors the Wheels pattern.
  const triggerHero =
    (isActive &&
      implicitPrimitive.startsWith('nasa-lattice-') &&
      NASA_LATTICE_HEROES[implicitPrimitive as NasaLatticeTopology]) ||
    Object.values(NASA_LATTICE_HEROES).find(Boolean);
  return (
    <DropdownMenu key={tab.value}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            pillWidthClass,
            'flex items-center justify-center gap-2 self-stretch overflow-hidden rounded-md border px-4 text-center text-sm transition-colors',
            isActive
              ? 'border-[#107d40] bg-[#070909] text-[#107d40] hover:border-[#107d40] hover:bg-[#0f3d24]'
              : 'border-[#737373] text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]',
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col items-center">
            {/* Shortened from tab.label ("NASA Lattice Primitives") — the full
                name doesn't fit two lines alongside a hero at the standardized
                pill width; the dropdown content still uses the full name. */}
            <span className="w-full truncate text-center leading-tight">
              NASA Lattice
            </span>
            <span className="mt-0.5 flex w-full items-center justify-center gap-1 text-xs leading-tight opacity-80">
              <span className="min-w-0 truncate">{activeLatticeLabel}</span>
              <ChevronDown className="h-3 w-3 flex-shrink-0" />
            </span>
          </div>
          {triggerHero && (
            <div className="flex w-10 flex-shrink-0 items-center justify-center self-stretch">
              <img
                src={triggerHero}
                alt=""
                aria-hidden="true"
                className="h-full object-contain"
              />
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={pickerContentClass}
        style={{
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }}
      >
        {NASA_LATTICE_TOPOLOGY_ORDER.map((t) => {
          const disabled = NASA_LATTICE_DISABLED.has(t);
          const hero = NASA_LATTICE_HEROES[t];
          const item = (
            <DropdownMenuItem
              key={t}
              disabled={disabled}
              onClick={() => handlePick(t)}
              className={cn(
                pickerDisablableItemBaseClass,
                disabled
                  ? pickerItemDisabledClass
                  : 'hover:cursor-pointer hover:bg-[#0f3d24]',
                !disabled && implicitPrimitive === t
                  ? pickerItemActiveClass
                  : pickerItemInactiveClass,
                // Heroed actives match the trigger's height (h-20
                // — same as Wheels / SUPIN active cards). Non-
                // heroed and disabled rows stay short to read as
                // either secondary options or "not a real option".
                hero && !disabled
                  ? 'flex h-20 items-center justify-between gap-3'
                  : 'flex items-center justify-between py-2',
              )}
            >
              <span className="text-sm font-medium">
                {IMPLICIT_PRIMITIVE_LABELS[t]}
              </span>
              {hero && !disabled && (
                <div className={pickerHeroWrapperClass}>
                  <img
                    src={hero}
                    alt=""
                    aria-hidden="true"
                    className="h-full object-contain"
                  />
                </div>
              )}
            </DropdownMenuItem>
          );
          if (!disabled) return item;
          // span wrapper needed: disabled DropdownMenuItem
          // swallows pointer events, so the tooltip never
          // opens without a sibling host.
          return (
            <Tooltip key={t}>
              <TooltipTrigger asChild>
                <span className="block">{item}</span>
              </TooltipTrigger>
              <TooltipContent side="right">Coming soon.</TooltipContent>
            </Tooltip>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
